import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Radio,
  RadioGroup,
  RadioProps,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextareaAutosize,
  TextField,
} from '@mui/material'
import { IMediaAccounts } from 'components/PostScheduling/PostScheduling'
import {
  Platform,
  useCreateInstagramAutomationToolMutation,
} from 'graphql/generated/graphql'
import InfoIcon from '@mui/icons-material/Info'
import CloseIcon from '@mui/icons-material/Close'
import React from 'react'
import { useSearchParams } from 'react-router-dom'
import SocialMediaIcon from 'components/SocialMediaIcon/SocialMediaIcon'
import Spinner from 'components/Spinner/Spinner'
import { usePageNavigation } from 'hooks/useNavigation'
import PostSchedulingStyle from 'components/PostScheduling/PostSchedulingStyle'
import SocialMediaRadio from 'components/PostScheduling/components/SocialMediaRadio'
import AutomationSetup from './AutomationSetup'
import { Sparkles, Check, ArrowRight, ArrowLeft } from 'lucide-react'

export default function CreateAutomation(): JSX.Element {
  const [searchParams] = useSearchParams()
  const platformFromUrl = searchParams.get('platform')

  const steps = ['Select Platform', 'Setup', 'Response', 'Overview']
  const [step, setStep] = React.useState(0)
  const [profileValue, setProfileValue] = React.useState<
    IMediaAccounts | undefined
  >()

  const [selectedPublication, setSelectedPublication] = React.useState<
    null | string
  >(null)

  const [keyword, setKeyword] = React.useState('')
  const [replySwitchValue, setReplySwitchValue] = React.useState(false)
  const [replies, setReplies] = React.useState<string[]>([])
  const [messages, setMessages] = React.useState<string[]>([])
  const replyInput = React.useRef<HTMLInputElement | null>(null)
  const messageInput = React.useRef<HTMLTextAreaElement | null>(null)
  const [title, setTitle] = React.useState('')
  const [selectedPlatform, setSelectedPlatform] = React.useState(
    platformFromUrl ?? 'Instagram'
  )
  const { navigateToAutomations } = usePageNavigation()

  const [workWithValue, setWorkWithValue] = React.useState<
    'specific-post' | 'all-publications'
  >('specific-post')

  function BpRadio(props: RadioProps): JSX.Element {
    return (
      <Radio
        disableRipple
        color="default"
        checkedIcon={<PostSchedulingStyle.BpCheckedIcon />}
        icon={<PostSchedulingStyle.BpIcon />}
        {...props}
        sx={{
          color: 'rgba(255, 255, 255, 0.6)',
          '&.Mui-checked': {
            color: '#a855f7',
          },
          '&:hover': {
            background: 'rgba(168, 85, 247, 0.1)',
          },
        }}
      />
    )
  }

  const [createInstagramAutomation, { loading: mutationLoading }] =
    useCreateInstagramAutomationToolMutation()

  const checkIfDisabled = (): boolean => {
    if (step === 1) {
      if (!profileValue) {
        return true
      }
      if (!title) {
        return true
      }
      if (workWithValue === 'specific-post' && !selectedPublication) {
        return true
      }
      if (!keyword) {
        return true
      }
    }
    if (step === 2) {
      if (messages.length === 0) {
        return true
      }
      if (replySwitchValue && replies.length === 0) {
        return true
      }
    }
    return false
  }

  const handleOnNext = (): void => {
    if (step === steps.length - 1) {
      if (profileValue?.id) {
        createInstagramAutomation({
          variables: {
            accountId: profileValue.id,
            commentReply: replies,
            keyword,
            message: messages,
            shouldReply: replySwitchValue,
            post_id: selectedPublication,
            title,
            platform:
              selectedPlatform === 'Facebook'
                ? Platform.Facebook
                : selectedPlatform === 'Youtube' 
                ? Platform.Youtube                
                : Platform.Instagram,
          },
        })
          .then(() => {
            navigateToAutomations(selectedPlatform)
          })
          .catch((e) => {
            console.log(e)
          })
      }
    } else {
      setStep(step + 1)
    }
  }

  const handleOnBack = (): void => {
    if (step === 2) {
      setReplySwitchValue(false)
      setReplies([])
      setMessages([])
    }
    setStep(step - 1)
  }

  const glassmorphicInput = {
    '& .MuiOutlinedInput-root': {
      background: 'rgba(30, 41, 59, 0.5)',
      backdropFilter: 'blur(12px)',
      borderRadius: '12px',
      color: '#ffffff',
      transition: 'all 0.3s ease',
      '& fieldset': {
        borderColor: 'rgba(168, 85, 247, 0.3)',
      },
      '&:hover': {
        background: 'rgba(30, 41, 59, 0.7)',
        '& fieldset': {
          borderColor: 'rgba(168, 85, 247, 0.5)',
        },
      },
      '&.Mui-focused': {
        background: 'rgba(30, 41, 59, 0.8)',
        '& fieldset': {
          borderColor: '#a855f7',
          borderWidth: '2px',
        },
      },
      '&.Mui-disabled': {
        background: 'rgba(30, 41, 59, 0.3)',
        color: 'rgba(255, 255, 255, 0.3)',
        cursor: 'not-allowed',
        '& fieldset': {
          borderColor: 'rgba(168, 85, 247, 0.15)',
        },
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)',
      '&.Mui-focused': {
        color: '#a855f7',
      },
      '&.Mui-disabled': {
        color: 'rgba(255, 255, 255, 0.3)',
      },
    },
  }

  if (mutationLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <Spinner />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        padding: '24px',
        minHeight: '100vh',
        color: '#ffffff',
      }}
    >
      {/* Header with Gradient Text */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box
            sx={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #a855f7, #ec4899)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(168, 85, 247, 0.4)',
            }}
          >
            <Sparkles size={24} color="#ffffff" />
          </Box>
          <Box>
            <h2
              style={{
                margin: 0,
                fontSize: '32px',
                fontWeight: 700,
                background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Create Automation Tool
            </h2>
            <p
              style={{
                margin: '4px 0 0 0',
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '14px',
              }}
            >
              Set up automated responses for your social media
            </p>
          </Box>
        </Box>

        {/* Modern Stepper */}
        <Stepper
          activeStep={step}
          sx={{
            padding: '24px',
            background: 'rgba(30, 41, 59, 0.4)',
            backdropFilter: 'blur(12px)',
            borderRadius: '16px',
            border: '1px solid rgba(168, 85, 247, 0.2)',
            '& .MuiStepLabel-label': {
              color: 'rgba(255, 255, 255, 0.5)',
              fontWeight: 500,
              '&.Mui-active': {
                color: '#ffffff',
                fontWeight: 600,
              },
              '&.Mui-completed': {
                color: 'rgba(255, 255, 255, 0.8)',
              },
            },
            '& .MuiStepIcon-root': {
              color: 'rgba(100, 116, 139, 0.5)',
              '&.Mui-active': {
                color: '#a855f7',
                filter: 'drop-shadow(0 0 8px rgba(168, 85, 247, 0.6))',
              },
              '&.Mui-completed': {
                color: '#10b981',
              },
            },
            '& .MuiStepConnector-line': {
              borderColor: 'rgba(100, 116, 139, 0.3)',
            },
            '& .Mui-completed .MuiStepConnector-line': {
              borderColor: '#10b981',
            },
          }}
        >
          {steps.map((stepLabel) => {
            return (
              <Step key={stepLabel}>
                <StepLabel>{stepLabel}</StepLabel>
              </Step>
            )
          })}
        </Stepper>
      </Box>

      {/* Step Content Container */}
      <Box
        sx={{
          flex: 1,
          padding: '32px',
          background: 'rgba(30, 41, 59, 0.3)',
          backdropFilter: 'blur(12px)',
          borderRadius: '20px',
          border: '1px solid rgba(168, 85, 247, 0.2)',
          minHeight: '500px',
        }}
      >
        {/* Step 0: Platform Selection */}
        {step === 0 && (
          <Box>
            <FormControl>
              <FormLabel
                id="social-media-radios"
                sx={{
                  fontSize: '24px',
                  mb: '20px',
                  color: '#ffffff',
                  fontWeight: 600,
                  '&.Mui-focused': {
                    color: '#ffffff',
                  },
                }}
              >
                Choose a platform:
              </FormLabel>
              <RadioGroup
                defaultValue="Instagram"
                aria-labelledby="social-media-radios"
                name="customized-radios"
                value={selectedPlatform}
                onChange={(e) => {
                  setSelectedPlatform(e.target.value)
                }}
                sx={{ gap: '12px' }}
              >
                <FormControlLabel
                  value="Instagram"
                  control={<BpRadio />}
                  label={
                    <SocialMediaRadio platform="Instagram" text="Instagram" />
                  }
                  sx={{
                    color: '#ffffff',
                    margin: 0,
                    padding: '16px 20px',
                    background: 'rgba(30, 41, 59, 0.4)',
                    borderRadius: '12px',
                    border: '1px solid rgba(168, 85, 247, 0.2)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'rgba(30, 41, 59, 0.6)',
                      borderColor: 'rgba(168, 85, 247, 0.4)',
                    },
                  }}
                />
                <FormControlLabel
                  value="Facebook"
                  control={<BpRadio />}
                  label={
                    <SocialMediaRadio platform="Facebook" text="Facebook" />
                  }
                  sx={{
                    color: '#ffffff',
                    margin: 0,
                    padding: '16px 20px',
                    background: 'rgba(30, 41, 59, 0.4)',
                    borderRadius: '12px',
                    border: '1px solid rgba(168, 85, 247, 0.2)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'rgba(30, 41, 59, 0.6)',
                      borderColor: 'rgba(168, 85, 247, 0.4)',
                    },
                  }}
                />
                <FormControlLabel
                  value="TikTok"
                  disabled
                  control={<BpRadio />}
                  label={
                    <SocialMediaRadio
                      platform="TikTok"
                      text="TikTok (Coming soon)"
                    />
                  }
                  sx={{
                    color: '#ffffff',
                    margin: 0,
                    padding: '16px 20px',
                    background: 'rgba(30, 41, 59, 0.2)',
                    borderRadius: '12px',
                    border: '1px solid rgba(168, 85, 247, 0.1)',
                    opacity: 0.5,
                    cursor: 'not-allowed',
                  }}
                />
                <FormControlLabel
                  value="Youtube"
                  control={<BpRadio />}
                  label={
                    <SocialMediaRadio
                      platform="Youtube"
                      text="Youtube"
                    />
                  }
                  sx={{
                    color: '#ffffff',
                    margin: 0,
                    padding: '16px 20px',
                    background: 'rgba(30, 41, 59, 0.2)',
                    borderRadius: '12px',
                    border: '1px solid rgba(168, 85, 247, 0.1)',
                    opacity: 0.5,
                    cursor: 'not-allowed',
                  }}
                />
                <FormControlLabel
                  value="Reddit"
                  disabled
                  control={<BpRadio />}
                  label={
                    <SocialMediaRadio
                      platform="Reddit"
                      text="Reddit (Coming soon)"
                    />
                  }
                  sx={{
                    color: '#ffffff',
                    margin: 0,
                    padding: '16px 20px',
                    background: 'rgba(30, 41, 59, 0.2)',
                    borderRadius: '12px',
                    border: '1px solid rgba(168, 85, 247, 0.1)',
                    opacity: 0.5,
                    cursor: 'not-allowed',
                  }}
                />
              </RadioGroup>
            </FormControl>
          </Box>
        )}

        {/* Step 1: Setup */}
        {step === 1 && (
          <AutomationSetup
            keyword={keyword}
            platform={
              selectedPlatform === 'Facebook'
                ? Platform.Facebook
                : Platform.Instagram
            }
            profileValue={profileValue}
            selectedPublication={selectedPublication}
            setKeyword={setKeyword}
            setProfileValue={setProfileValue}
            setSelectedPublication={setSelectedPublication}
            setTitle={setTitle}
            setWorkWithValue={setWorkWithValue}
            title={title}
            workWithValue={workWithValue}
          />
        )}

        {/* Step 2: Response */}
        {step === 2 && (
          <Box>
            {/* Reply Toggle */}
            <Box
              sx={{
                padding: '20px 24px',
                background: 'rgba(30, 41, 59, 0.4)',
                borderRadius: '12px',
                border: '1px solid rgba(168, 85, 247, 0.2)',
                marginBottom: '24px',
              }}
            >
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={replySwitchValue}
                      onChange={(e) => {
                        setReplySwitchValue(e.target.checked)
                        if (!e.target.checked) {
                          setReplies([])
                        }
                      }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#a855f7',
                          '&:hover': {
                            backgroundColor: 'rgba(168, 85, 247, 0.08)',
                          },
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                          {
                            backgroundColor: '#a855f7',
                          },
                        '& .MuiSwitch-track': {
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span style={{ fontSize: '16px', fontWeight: 600 }}>
                        Reply in Comments
                      </span>
                    </Box>
                  }
                  sx={{ color: '#ffffff', margin: 0 }}
                />
              </FormGroup>
            </Box>

            {/* Comment Replies Section */}
            {replySwitchValue && (
              <Box
                sx={{
                  padding: '24px',
                  background: 'rgba(30, 41, 59, 0.4)',
                  borderRadius: '12px',
                  border: '1px solid rgba(168, 85, 247, 0.2)',
                  marginBottom: '24px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '16px',
                  }}
                >
                  <InfoIcon
                    sx={{
                      color: '#a855f7',
                      fontSize: '18px',
                    }}
                  />
                  <Box
                    sx={{
                      fontSize: '14px',
                      color: 'rgba(255, 255, 255, 0.7)',
                    }}
                  >
                    Create multiple replies to send random responses.
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: '12px',
                    marginBottom: '16px',
                  }}
                >
                  <TextField
                    inputRef={replyInput}
                    placeholder="Enter a comment reply..."
                    sx={{
                      flex: 1,
                      ...glassmorphicInput,
                      '& > div > fieldset': {
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                      },
                    }}
                    disabled={replies.length === 3}
                  />
                  <Button
                    variant="contained"
                    disabled={replies.length === 3}
                    sx={{
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                      background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                      minWidth: '160px',
                      fontWeight: 600,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #9333ea, #db2777)',
                      },
                      '&.Mui-disabled': {
                        background: 'rgba(168, 85, 247, 0.2)',
                        color: 'rgba(255, 255, 255, 0.3)',
                      },
                    }}
                    onClick={() => {
                      if (replyInput.current?.value) {
                        const value = replyInput.current.value
                        replyInput.current.value = ''
                        setReplies((prev) => {
                          return [...prev, value]
                        })
                      }
                    }}
                  >
                    Add Reply
                  </Button>
                </Box>
                {replies.map((reply) => {
                  return (
                    <Box
                      key={reply}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '10px',
                        background: 'rgba(15, 23, 42, 0.6)',
                        padding: '14px 18px',
                        borderRadius: '10px',
                        border: '1px solid rgba(168, 85, 247, 0.15)',
                        transition: 'all 0.2s',
                        '&:hover': {
                          background: 'rgba(15, 23, 42, 0.8)',
                          borderColor: 'rgba(168, 85, 247, 0.3)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          color: '#ffffff',
                          flex: 1,
                          fontSize: '14px',
                        }}
                      >
                        {reply}
                      </Box>
                      <CloseIcon
                        sx={{
                          color: '#ef4444',
                          cursor: 'pointer',
                          fontSize: '20px',
                          transition: 'all 0.2s',
                          '&:hover': {
                            color: '#dc2626',
                            transform: 'scale(1.1)',
                          },
                        }}
                        onClick={() => {
                          setReplies((prev) => {
                            return prev.filter((x) => x !== reply)
                          })
                        }}
                      />
                    </Box>
                  )
                })}
              </Box>
            )}

            {/* Direct Messages Section */}
            <Box
              sx={{
                padding: '24px',
                background: 'rgba(30, 41, 59, 0.4)',
                borderRadius: '12px',
                border: '1px solid rgba(168, 85, 247, 0.2)',
              }}
            >
              <h4
                style={{
                  margin: '0 0 16px 0',
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#ffffff',
                }}
              >
                Send Direct Message
              </h4>
              <Box
                sx={{
                  display: 'flex',
                  gap: '12px',
                  marginBottom: '16px',
                }}
              >
                <TextareaAutosize
                  ref={messageInput}
                  minRows={7}
                  placeholder="Enter a direct message..."
                  style={{
                    width: '100%',
                    maxWidth: '500px',
                    maxHeight: '200px',
                    padding: '14px',
                    color: '#ffffff',
                    fontSize: '14px',
                    border: messages.length === 3 
                      ? '1px solid rgba(168, 85, 247, 0.15)' 
                      : '1px solid rgba(168, 85, 247, 0.3)',
                    background: messages.length === 3 
                      ? 'rgba(30, 41, 59, 0.3)' 
                      : 'rgba(30, 41, 59, 0.5)',
                    backdropFilter: 'blur(12px)',
                    borderRadius: '12px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    transition: 'all 0.3s ease',
                  }}
                  disabled={messages.length === 3}
                />
                <Button
                  disabled={messages.length === 3}
                  variant="contained"
                  sx={{
                    alignSelf: 'flex-end',
                    background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                    minWidth: '140px',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #9333ea, #db2777)',
                    },
                    '&.Mui-disabled': {
                      background: 'rgba(168, 85, 247, 0.2)',
                      color: 'rgba(255, 255, 255, 0.3)',
                    },
                  }}
                  onClick={() => {
                    if (messageInput.current?.value) {
                      const value = messageInput.current.value
                      messageInput.current.value = ''
                      setMessages((prev) => {
                        return [...prev, value]
                      })
                    }
                  }}
                >
                  Add Message
                </Button>
              </Box>
              {messages.map((message) => {
                return (
                  <Box
                    key={message}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '10px',
                      background: 'rgba(15, 23, 42, 0.6)',
                      padding: '14px 18px',
                      borderRadius: '10px',
                      border: '1px solid rgba(168, 85, 247, 0.15)',
                      transition: 'all 0.2s',
                      '&:hover': {
                        background: 'rgba(15, 23, 42, 0.8)',
                        borderColor: 'rgba(168, 85, 247, 0.3)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        color: '#ffffff',
                        flex: 1,
                        fontSize: '14px',
                      }}
                    >
                      {message}
                    </Box>
                    <CloseIcon
                      sx={{
                        color: '#ef4444',
                        cursor: 'pointer',
                        fontSize: '20px',
                        transition: 'all 0.2s',
                        '&:hover': {
                          color: '#dc2626',
                          transform: 'scale(1.1)',
                        },
                      }}
                      onClick={() => {
                        setMessages((prev) => {
                          return prev.filter((x) => x !== message)
                        })
                      }}
                    />
                  </Box>
                )
              })}
            </Box>
          </Box>
        )}

        {/* Step 3: Overview */}
        {step === 3 && (
          <Box sx={{ color: '#ffffff' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                marginBottom: '24px',
              }}
            >
              <Box
                sx={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Check size={20} color="#ffffff" />
              </Box>
              <h2
                style={{
                  margin: 0,
                  fontSize: '28px',
                  fontWeight: 700,
                }}
              >
                Review Your Automation
              </h2>
            </Box>
            <Divider sx={{ borderColor: 'rgba(168, 85, 247, 0.2)', mb: 3 }} />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Username */}
              <Box
                sx={{
                  padding: '20px 24px',
                  background: 'rgba(30, 41, 59, 0.4)',
                  borderRadius: '12px',
                  border: '1px solid rgba(168, 85, 247, 0.2)',
                }}
              >
                <Box sx={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.5)', mb: 1 }}>
                  Account
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SocialMediaIcon platformName={selectedPlatform} width={24} />
                  <Box sx={{ fontSize: '16px', fontWeight: 600 }}>
                    {profileValue?.accountName}
                  </Box>
                </Box>
              </Box>

              {/* Publication Type */}
              <Box
                sx={{
                  padding: '20px 24px',
                  background: 'rgba(30, 41, 59, 0.4)',
                  borderRadius: '12px',
                  border: '1px solid rgba(168, 85, 247, 0.2)',
                }}
              >
                <Box sx={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.5)', mb: 1 }}>
                  Publication Type
                </Box>
                <Box sx={{ fontSize: '16px', fontWeight: 600 }}>
                  {workWithValue === 'all-publications'
                    ? '✨ All Publications'
                    : '📍 Specific Publication'}
                </Box>
              </Box>

              {/* Keyword */}
              <Box
                sx={{
                  padding: '20px 24px',
                  background: 'rgba(30, 41, 59, 0.4)',
                  borderRadius: '12px',
                  border: '1px solid rgba(168, 85, 247, 0.2)',
                }}
              >
                <Box sx={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.5)', mb: 1 }}>
                  Trigger Keyword
                </Box>
                <Box
                  sx={{
                    fontSize: '18px',
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                  }}
                >
                  {keyword}
                </Box>
              </Box>

              {/* Comment Replies */}
              <Box
                sx={{
                  padding: '20px 24px',
                  background: 'rgba(30, 41, 59, 0.4)',
                  borderRadius: '12px',
                  border: '1px solid rgba(168, 85, 247, 0.2)',
                }}
              >
                <Box sx={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.5)', mb: 2 }}>
                  Comment Replies
                </Box>
                {replySwitchValue ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {replies.map((reply) => (
                      <Box
                        key={reply}
                        sx={{
                          padding: '12px 16px',
                          background: 'rgba(15, 23, 42, 0.6)',
                          borderRadius: '10px',
                          border: '1px solid rgba(168, 85, 247, 0.15)',
                          fontSize: '14px',
                        }}
                      >
                        {reply}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '14px' }}>
                    No comment replies configured
                  </Box>
                )}
              </Box>

              {/* Direct Messages */}
              <Box
                sx={{
                  padding: '20px 24px',
                  background: 'rgba(30, 41, 59, 0.4)',
                  borderRadius: '12px',
                  border: '1px solid rgba(168, 85, 247, 0.2)',
                }}
              >
                <Box sx={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.5)', mb: 2 }}>
                  Direct Messages
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {messages.map((message) => (
                    <Box
                      key={message}
                      sx={{
                        padding: '12px 16px',
                        background: 'rgba(15, 23, 42, 0.6)',
                        borderRadius: '10px',
                        border: '1px solid rgba(168, 85, 247, 0.15)',
                        fontSize: '14px',
                      }}
                    >
                      {message}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Navigation Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '20px 24px',
          background: 'rgba(30, 41, 59, 0.3)',
          backdropFilter: 'blur(12px)',
          borderRadius: '16px',
          border: '1px solid rgba(168, 85, 247, 0.2)',
        }}
      >
        {step !== 0 && (
          <Button
            onClick={handleOnBack}
            startIcon={<ArrowLeft size={18} />}
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              padding: '10px 24px',
              borderRadius: '10px',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                color: '#ffffff',
                background: 'rgba(255, 255, 255, 0.05)',
              },
            }}
          >
            Back
          </Button>
        )}
        <Button
          disabled={checkIfDisabled()}
          onClick={handleOnNext}
          endIcon={step === steps.length - 1 ? <Check size={18} /> : <ArrowRight size={18} />}
          variant="contained"
          sx={{
            marginLeft: 'auto',
            minWidth: '140px',
            padding: '10px 28px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #a855f7, #ec4899)',
            fontWeight: 600,
            fontSize: '15px',
            textTransform: 'none',
            boxShadow: '0 4px 12px rgba(168, 85, 247, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #9333ea, #db2777)',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 16px rgba(168, 85, 247, 0.4)',
            },
            '&.Mui-disabled': {
              background: 'rgba(168, 85, 247, 0.2)',
              color: 'rgba(255, 255, 255, 0.3)',
              boxShadow: 'none',
            },
            transition: 'all 0.3s ease',
          }}
        >
          {step === steps.length - 1 ? 'Create Automation' : 'Next Step'}
        </Button>
      </Box>
    </Box>
  )
}